import { useMemo, useState } from 'react'
import { Clock, Coins, Navigation } from 'lucide-react'
import { CanonicalLoad } from '@numeo/types-core'
import { Button, Modal } from '@/components/ds2'
import GoogleMapsLoader from '@/components/GoogleMapsLoader'
import { useTollRoutes } from 'features/load-hub/hooks/useTollRoutes'
import { cityLabel } from '../utils/format'
import { NearbyBoxTruckDriver } from '../services/boxTruckLoadsApi'
import { LatLng, NearbyDriverRouteMap, RouteLegs, toLatLng } from './NearbyDriverRouteMap'

const InfoCard = ({ icon, label, value, accent, className }: { icon: React.ReactNode; label: string; value: string; accent?: boolean; className?: string }) => (
    <div className={`flex items-center gap-3 rounded-xl bg-background px-4 py-3 ${className ?? ''}`}>
        <span
            className={`flex size-9 shrink-0 items-center justify-center rounded-full ${accent ? 'bg-accent-soft text-accent-soft-foreground' : 'bg-default text-foreground-muted'}`}
        >
            {icon}
        </span>
        <div className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-foreground-muted">{label}</div>
            <div className="truncate text-sm font-semibold text-foreground">{value}</div>
        </div>
    </div>
)

/** ETA card value: an explicit failure reads "Route unavailable", a pending result "Calculating…". */
const legValue = (route: RouteLegs, leg: 'toOrigin' | 'toDest'): string => {
    if (route.failed) return 'Route unavailable'
    const l = route[leg]
    return l ? `${l.duration} · ${l.distance}` : 'Calculating…'
}

/** The reused load-hub toll flow, laid out as a bar BELOW the map: a ds2 "Get Tolls" button that fetches the
 *  HERE-backed toll routes on demand, then the selectable per-route cost chips + a Hide toggle. */
const TollBar = ({ toll }: { toll: ReturnType<typeof useTollRoutes> }) => (
    <div className="flex min-h-9 flex-wrap items-center gap-2">
        {!toll.showTollBoxes ? (
            <Button variant="primary" size="md" loading={toll.isTollLoading} onClick={toll.handleGetTolls} prefix={<Coins className="h-4 w-4" />}>
                Get Tolls
            </Button>
        ) : (
            <>
                <span className="text-[11px] font-semibold uppercase tracking-wider text-foreground-muted">Tolls</span>
                <div className="flex flex-wrap gap-2">
                    {toll.tollRoutes.slice(0, 4).map((route, index) => {
                        const isSelected = index === toll.selectedRouteIndex
                        const amount = typeof route.tollCost?.amount === 'number' ? route.tollCost.amount : 0
                        return (
                            <button
                                key={route.routeId}
                                type="button"
                                onClick={() => toll.handleTollBoxClick(index)}
                                className={`rounded-2xl px-3.5 py-1.5 text-left transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface ${
                                    isSelected ? 'bg-accent text-accent-foreground' : 'bg-default text-default-foreground hover:bg-default-hover'
                                }`}
                            >
                                <div className="text-sm font-semibold leading-tight">${amount.toFixed(2)}</div>
                                <div className="text-[10px] leading-tight opacity-80">{Math.round(route.distance?.miles || 0)} mi</div>
                            </button>
                        )
                    })}
                </div>
                <Button variant="secondary" size="md" onClick={toll.hideTollBoxes}>
                    Hide
                </Button>
            </>
        )}
        {toll.tollError && <span className="text-xs font-medium text-danger">{toll.tollError}</span>}
    </div>
)

/**
 * Operator route map for a nearby driver: driver location → load pickup → load delivery on a Google map, with
 * the driver→pickup drive-time ETA (Directions). Tolls are NOT computed automatically — the operator clicks
 * the reused load-hub "Get Tolls" control (below the map), which fetches the HERE-backed toll routes and
 * overlays them.
 *
 * Uses the ds2 Modal with `disableTransform` — a CSS transform on a Google Maps ancestor breaks tile rendering,
 * so the modal centers via flexbox instead.
 */
export const NearbyDriverRouteModal = ({ driver, load, onClose }: { driver: NearbyBoxTruckDriver; load: CanonicalLoad; onClose: () => void }) => {
    const [route, setRoute] = useState<RouteLegs>({ toOrigin: null, toDest: null, failed: false })
    // Reverse-geocoded driver locality, used only when the driver profile has no city/state.
    const [driverPlace, setDriverPlace] = useState<string | null>(null)

    // Stable coord objects (memoized on the raw primitives) so the map/route effect runs ONCE, not on every
    // leg/toll state update.
    const driverLL = useMemo<LatLng | null>(() => toLatLng(driver.lat, driver.lng), [driver.lat, driver.lng])
    const originLL = useMemo<LatLng | null>(() => toLatLng(load.pickupLat, load.pickupLon), [load.pickupLat, load.pickupLon])
    const destLL = useMemo<LatLng | null>(() => toLatLng(load.deliveryLat, load.deliveryLon), [load.deliveryLat, load.deliveryLon])
    const canRoute = driverLL && originLL && destLL

    const { pickupCity, pickupStateProv, deliveryCity, deliveryStateProv } = load
    const driverProfilePlace = cityLabel(driver.city, driver.state)
    const driverHeaderPlace = driver.city || driver.state ? driverProfilePlace : (driverPlace ?? 'Locating…')

    // Reused load-hub on-demand toll flow (HERE-backed). Tolls are only fetched when the operator clicks
    // "Get Tolls" — never by default.
    const tollState = useTollRoutes({
        origin: { lat: originLL?.lat ?? 0, lng: originLL?.lng ?? 0, city: pickupCity ?? '', state: pickupStateProv ?? '' },
        destination: { lat: destLL?.lat ?? 0, lng: destLL?.lng ?? 0, city: deliveryCity ?? '', state: deliveryStateProv ?? '' },
        hasFinalCoords: !!(originLL && destLL),
    })

    const header = (
        <div className="min-w-0">
            <div className="text-sm font-semibold text-foreground">Route · {driver.name || 'Driver'}</div>
            <div className="truncate text-xs text-foreground-muted">
                {driverHeaderPlace} → {cityLabel(pickupCity, pickupStateProv)} → {cityLabel(deliveryCity, deliveryStateProv)}
            </div>
        </div>
    )

    return (
        <Modal open onClose={onClose} disableTransform size="lg" style={{ width: 'min(1500px, 97vw)' }} header={header}>
            {!canRoute ? (
                <div className="rounded-xl border border-border px-3 py-10 text-center text-sm text-foreground-muted">
                    Coordinates unavailable for the driver or the load — can&apos;t draw the route.
                </div>
            ) : (
                <>
                    <div className="w-full overflow-hidden rounded-xl border border-border">
                        <GoogleMapsLoader>
                            <NearbyDriverRouteMap
                                driver={driverLL}
                                origin={originLL}
                                destination={destLL}
                                tollRoutes={tollState.tollRoutes}
                                selectedRouteIndex={tollState.selectedRouteIndex}
                                showTolls={tollState.showTollBoxes}
                                onResult={setRoute}
                                onDriverPlace={setDriverPlace}
                            />
                        </GoogleMapsLoader>
                    </div>
                    {/* One row below the map: the two ETA cards, and the reused Get Tolls control on the right. */}
                    <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                        <InfoCard
                            className="flex-1 sm:min-w-[220px]"
                            icon={<Navigation size={16} />}
                            label="Driver → Pickup (ETA)"
                            value={legValue(route, 'toOrigin')}
                            accent
                        />
                        <InfoCard className="flex-1 sm:min-w-[220px]" icon={<Clock size={16} />} label="Pickup → Delivery" value={legValue(route, 'toDest')} />
                        <div className="shrink-0 sm:ml-auto">
                            <TollBar toll={tollState} />
                        </div>
                    </div>
                </>
            )}
        </Modal>
    )
}
